import { useState, useEffect } from "react";
import { useApi } from "../../../utils/Api";
import Translate from "../../../utils/Translate";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import {
  MenuItem,
  Switch,
  Stack,
  Select,
  TextField,
  Container,
  InputLabel,
  FormControl,
  Tooltip,
  Typography,
  Box,
} from "@mui/material";
import Preloader from "../../Preloader";
import { useNavigate, useLocation } from "react-router-dom";
import { actorResourceTypes } from "../../../utils/actorResourceTypes";
import { accessKeys, hasAccess } from "../../../utils/userAccess";

const trueString = "1",
  falseString = "0";

function displayAsNumberField(resource) {
  return (
    !isNaN(resource.value) &&
    (resource.type === actorResourceTypes.integer || resource.type == actorResourceTypes.float)
  );
}

const ActorResources = ({ actorId }) => {
  const [actorResourceValues, setActorResourceValues] = useState(null);
  const [actorParCodes, setActorParCodes] = useState(null);
  const [isLoading, setIsLoading] = useState();
  const [isDirty, setIsDirty] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const api = useApi();

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        await getActorResourceValues();
        const parCodesResponse = await api.fetch(
          `${import.meta.env.VITE_MAIN_URL}actors/${actorId}/par-codes?parCodeGroupSource=SCB`,
          false,
          "GET",
        );
        setActorParCodes(parCodesResponse ?? []);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const closeAndReload = () => {
    const hash = window.location.hash.endsWith("#dialog")
      ? window.location.hash.substring(0, window.location.hash.length - 7)
      : window.location.hash;
    const loc = encodeURIComponent(`${location.pathname}${location.search}${hash}`);
    navigate(`/redirect/${loc}`, { replace: true });
  };

  async function getActorResourceValues() {
    const response = await api.fetch(`${import.meta.env.VITE_MAIN_URL}actors/${actorId}/resources`, false, "GET");
    setActorResourceValues(
      response.sort((a, b) =>
        a.sortOrder - b.sortOrder === 0 ? a.name?.localeCompare(b.name) : a.sortOrder - b.sortOrder,
      ),
    );
  }

  async function saveActorResourceValues() {
    setIsLoading(true);
    let response = null;
    try {
      response = await api.fetch(
        `${import.meta.env.VITE_MAIN_URL}actors/${actorId}/resources`,
        actorResourceValues,
        "POST",
      );

      setActorResourceValues(
        response.sort((a, b) =>
          a.sortOrder - b.sortOrder === 0 ? a.name?.localeCompare(b.name) : a.sortOrder - b.sortOrder,
        ),
      );

      closeAndReload();
    } finally {
      setIsLoading(false);
    }
  }

  function changeResourceValue(actorResourceId, newValue) {
    const actorResource = actorResourceValues.find((x) => x.actorResourceId === actorResourceId);
    actorResource.value = newValue;
    setIsDirty(true);
    setActorResourceValues([...actorResourceValues]);
  }

  if (isLoading) {
    return (
      <div>
        <Preloader />
      </div>
    );
  }

  return (
    <Dialog
      open={true}
      onClose={closeAndReload}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="sm"
      fullWidth={true}
    >
      <DialogTitle id="alert-dialog-title">
        <Stack direction="row">
          <Container disableGutters>{Translate.get("ActorResources")}</Container>
          <ManageAccountsIcon paddingTop="2rem" />
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={{ xs: 1.5, sm: 2.5 }} sx={{ paddingTop: "2rem" }}>
          {actorResourceValues && actorResourceValues.length < 1
            ? Translate.get("ActorResourceValuesEmptyState")
            : actorResourceValues?.map((resource) => (
                <div key={resource.name}>
                  {resource.comboValues ? (
                    <FormControl sx={{ width: "100%" }}>
                      <InputLabel>{resource.name}</InputLabel>
                      <Select
                        label={resource.name}
                        value={resource.value}
                        onChange={(evt) => changeResourceValue(resource.actorResourceId, evt.target.value)}
                      >
                        {[
                          ...(resource.comboValues.find((itm) => itm === resource.value)
                            ? []
                            : [
                                {
                                  value: resource.value,
                                  disabled: true,
                                },
                              ]), // lägg till ett val för det satta värdet om det saknas i defintionen, sätt det tillagda värdet som disabled
                          ...resource.comboValues.map((x) => ({ value: x })),
                        ].map((x, idx) => (
                          <MenuItem key={`${x.value}-${idx}`} disabled={x.disabled} value={x.value}>
                            {x.value}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : resource.type === actorResourceTypes.boolean ? (
                    <Tooltip
                      placement="top-end"
                      title={`${resource.name} : ${resource.value === trueString ? Translate.get("Yes") : Translate.get("No")}`}
                    >
                      <Stack justifyContent="space-between" direction="row">
                        <Container disableGutters sx={{ paddingTop: ".5rem", paddingLeft: ".7rem" }}>
                          {resource.name}
                        </Container>
                        <Switch
                          checked={resource.value === trueString}
                          name={resource.name}
                          disabled={resource.name === "Elektronisk Faktura"} // UGLY: fixa efter att interna lagts ner
                          onChange={(evt) =>
                            changeResourceValue(resource.actorResourceId, evt.target.checked ? trueString : falseString)
                          }
                        />
                      </Stack>
                    </Tooltip>
                  ) : (
                    <TextField
                      label={resource.name}
                      sx={{ width: "100%" }}
                      name={resource.name}
                      value={resource.value}
                      type={displayAsNumberField(resource) ? "number" : "text"}
                      inputProps={{ maxLength: 255 }}
                      onChange={(evt) => changeResourceValue(resource.actorResourceId, evt.target.value)}
                    />
                  )}
                </div>
              ))}
        </Stack>
        {hasAccess(accessKeys.isMASystem) && actorParCodes && actorParCodes.length > 0 && (
          <>
            <Typography variant="subtitle1" sx={{ mb: 1, mt: 3 }}>
              {Translate.get("ActorParCodesFromScb")}
            </Typography>
            <Stack spacing={{ xs: 2, sm: 1 }}>
              {[...actorParCodes]
                .sort((a, b) => (a.parCodeGroupName ?? "").localeCompare(b.parCodeGroupName ?? ""))
                .map((pc) => (
                  <Stack
                    key={pc.parCodeId}
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent={{ xs: "flex-start", sm: "space-between" }}
                  >
                    <Typography variant="body2">{pc.parCodeGroupName}:</Typography>
                    <Typography variant="body2">{pc.description}</Typography>
                  </Stack>
                ))}
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions
        style={{ justifyContent: "space-between", marginLeft: "1em", marginRight: "1em", marginBottom: "1em" }}
      >
        <Button onClick={closeAndReload}>{Translate.get("Close")}</Button>
        <Button variant="contained" onClick={saveActorResourceValues} disabled={!isDirty}>
          {Translate.get("Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ActorResources;
